import { useForm } from "@tanstack/react-form";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { apiErrorHandler } from "@/core/api/api-error-handler";
import { MutationConfirmPreview } from "@/core/components/shared/preview-action";
import { Button } from "@/core/components/ui/button";
import { ButtonSpinner } from "@/core/components/ui/buttonStyled/ButtonSpinner.component";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/core/components/ui/dialog";
import { Input } from "@/core/components/ui/input";
import { Label } from "@/core/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/core/components/ui/select";
import useTypedTranslation, {
  type TFunctionType,
} from "@/core/hooks/use-typed-translation";
import { notifSilence, notifSilenceError } from "@/core/utils/func/notifApp";
import { DEFAULT_CURRENCY, majorToMinor } from "@/core/utils/func/money-minor";
import { useCreatePayrollBatch } from "@/features/payroll/api/use-create-payroll-batch";
import type { PayrollBatchCreateRequestDto } from "@/features/payroll/api/dto";
import { PAYROLL_FEE_PAYERS } from "@/features/payroll/api/enums";
import {
  createPayrollBatchSchema,
  type PayrollBatchFormValues,
} from "@/features/payroll/schemas/payroll-batch.schema";

const EMPTY_VALUES: PayrollBatchFormValues = {
  label: "",
  feePayer: "SENDER",
  lines: [
    {
      employeeName: "",
      employeeReference: "",
      phone: "",
      amount: 0,
    },
  ],
};

/**
 * Création d'une paie (`POST /PayrollBatches/add`, 201).
 *
 * UX pragmatique : le dialog crée une paie avec **une seule ligne** (label +
 * feePayer + un salarié initial). Les lignes supplémentaires s'ajoutent depuis
 * la page détail (via `useAddPayrollLine`). Les paies volumineuses passent par
 * l'import Excel (`PayrollBatchImportDialog`) — c'est la voie recommandée du
 * guide. Preview `MutationConfirmPreview` (opération sans frais à ce stade :
 * pas d'argent en jeu avant simulate+launch).
 */
export function PayrollBatchCreateDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTypedTranslation();
  const { i18n } = useTranslation();
  const tPayroll = (key: string) => t(key as TFunctionType);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const schema = useMemo(() => createPayrollBatchSchema(), [i18n.language]);

  const createBatch = useCreatePayrollBatch();
  const pending = createBatch.isPending;
  const [preview, setPreview] = useState<PayrollBatchCreateRequestDto | null>(
    null
  );

  const form = useForm({
    defaultValues: EMPTY_VALUES,
    onSubmit: async ({ value }) => {
      const parsed = schema.safeParse(value);
      if (!parsed.success) return;
      const payload: PayrollBatchCreateRequestDto = {
        label: parsed.data.label,
        feePayer: parsed.data.feePayer,
        lines: parsed.data.lines.map((line) => ({
          employeeName: line.employeeName,
          employeeReference: line.employeeReference || null,
          phone: line.phone,
          amountMinor: majorToMinor(line.amount, DEFAULT_CURRENCY),
        })),
      };
      setPreview(payload);
    },
  });

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t("payroll.batches.create.title")}</DialogTitle>
          </DialogHeader>

          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();
              void form.handleSubmit();
            }}
          >
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <form.Field name="label">
                {(field) => (
                  <div className="space-y-1.5 sm:col-span-2">
                    <Label htmlFor="payroll-batch-label">
                      {t("payroll.batches.form.label")}
                    </Label>
                    <Input
                      id="payroll-batch-label"
                      maxLength={120}
                      placeholder={t("payroll.batches.form.labelPlaceholder")}
                      value={field.state.value}
                      onBlur={field.handleBlur}
                      onChange={(e) => field.handleChange(e.target.value)}
                    />
                  </div>
                )}
              </form.Field>

              <form.Field name="feePayer">
                {(field) => (
                  <div className="space-y-1.5 sm:col-span-2">
                    <Label htmlFor="payroll-batch-fee-payer">
                      {t("payroll.batches.form.feePayer")}
                    </Label>
                    <Select
                      value={field.state.value}
                      onValueChange={(value) =>
                        field.handleChange(
                          value as PayrollBatchFormValues["feePayer"]
                        )
                      }
                    >
                      <SelectTrigger id="payroll-batch-fee-payer">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {PAYROLL_FEE_PAYERS.map((value) => (
                          <SelectItem key={value} value={value}>
                            {t(
                              `payroll.enums.feePayer.${value}` as TFunctionType
                            )}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      {t(
                        `payroll.batches.form.feePayerHint.${field.state.value}` as TFunctionType
                      )}
                    </p>
                  </div>
                )}
              </form.Field>
            </div>

            <div className="rounded-md border p-4">
              <h3 className="text-sm font-medium text-foreground">
                {t("payroll.batches.form.firstLineTitle")}
              </h3>
              <p className="mb-3 text-xs text-muted-foreground">
                {t("payroll.batches.form.firstLineHint")}
              </p>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <form.Field name="lines[0].employeeName">
                  {(field) => (
                    <div className="space-y-1.5">
                      <Label htmlFor="line-0-name">
                        {t("payroll.lines.form.employeeName")}
                      </Label>
                      <Input
                        id="line-0-name"
                        maxLength={120}
                        value={field.state.value}
                        onChange={(e) => field.handleChange(e.target.value)}
                      />
                    </div>
                  )}
                </form.Field>

                <form.Field name="lines[0].employeeReference">
                  {(field) => (
                    <div className="space-y-1.5">
                      <Label htmlFor="line-0-ref">
                        {t("payroll.lines.form.employeeReference")}
                      </Label>
                      <Input
                        id="line-0-ref"
                        maxLength={80}
                        placeholder={t(
                          "payroll.lines.form.employeeReferencePlaceholder"
                        )}
                        value={field.state.value ?? ""}
                        onChange={(e) => field.handleChange(e.target.value)}
                      />
                    </div>
                  )}
                </form.Field>

                <form.Field name="lines[0].phone">
                  {(field) => (
                    <div className="space-y-1.5">
                      <Label htmlFor="line-0-phone">
                        {t("payroll.lines.form.phone")}
                      </Label>
                      <Input
                        id="line-0-phone"
                        className="notranslate"
                        placeholder="+22462…"
                        value={field.state.value}
                        onChange={(e) => field.handleChange(e.target.value)}
                      />
                    </div>
                  )}
                </form.Field>

                <form.Field name="lines[0].amount">
                  {(field) => (
                    <div className="space-y-1.5">
                      <Label htmlFor="line-0-amount">
                        {t("payroll.lines.form.amount")}
                      </Label>
                      <Input
                        id="line-0-amount"
                        type="number"
                        min={1}
                        className="notranslate"
                        value={field.state.value || ""}
                        onChange={(e) =>
                          field.handleChange(
                            Number.isNaN(e.target.valueAsNumber)
                              ? 0
                              : e.target.valueAsNumber
                          )
                        }
                      />
                    </div>
                  )}
                </form.Field>
              </div>
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={pending}
              >
                {t("common.cancel")}
              </Button>
              <Button type="submit" disabled={pending}>
                {pending ? <ButtonSpinner /> : null}
                {t("common.confirm")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <MutationConfirmPreview
        open={preview !== null}
        onOpenChange={(isOpen) => {
          if (!isOpen) setPreview(null);
        }}
        data={preview}
        title={t("payroll.batches.create.previewTitle")}
        description={t("payroll.batches.create.previewDescription")}
        t={tPayroll}
        pending={pending}
        onConfirm={() => {
          if (!preview) return;
          createBatch.mutate(preview, {
            onSuccess: () => {
              notifSilence(t("payroll.batches.feedback.created"));
              setPreview(null);
              onOpenChange(false);
            },
            onError: (error) =>
              notifSilenceError(apiErrorHandler(error) || error.message),
          });
        }}
      />
    </>
  );
}
